import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
    "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-20",
    {
        variants: {
            variant: {
                default:
                    "bg-primary text-primary-foreground shadow hover:bg-primary/70",
                success:
                    "bg-success/80 text-primary-foreground shadow hover:bg-success/50",
                cc: "bg-accent text-primary-foreground shadow data-[state=open]:bg-muted hover:bg-primary/70 !h-auto !text-xs",
                navigation:
                    "bg-transparent text-copy-lighter hover:text-foreground hover:bg-accent items-center justify-start flex gap-1",
                navigationAccent:
                    "bg-accent text-foreground hover:bg-popover items-center justify-start flex gap-1",
                destructive:
                    "bg-destructive text-foreground shadow-sm hover:bg-destructive/90",
                outline:
                    "border bg-transparent shadow-sm hover:bg-accent hover:text-accent-foreground",
                secondary:
                    "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
                tertiary:
                    " bg-tertiary text-foreground shadow-sm hover:bg-tertiary/80",
                ghost: "hover:bg-accent hover:text-accent-foreground",
                link: "underline-offset-4 underline hover:text-primary",
                staffbar:
                    "inline-flex items-center rounded-md border !px-2.5 !py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-card text-foreground capitalize border-accent hover:bg-accent !h-auto",
                sidebar_active:
                    "bg-muted text-primary-foreground shadow hover:bg-accent justify-start gap-1",
                sidebar_inactive:
                    "bg-transparent text-muted-foreground shadow hover:bg-accent justify-start gap-1",
            },
            size: {
                default: "h-9 px-4 py-2",
                sm: "h-8 rounded-md px-3 text-xs",
                lg: "h-10 rounded-md px-8",
                icon: "h-auto w-auto p-1",
                navigation: "px-0 pl-2 pr-9 py-2 h-auto",
                link: "px-0 py-0 h-auto text-inherit",
                sidenav: "p-2 h-auto",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : "button";
        return (
            <Comp
                className={cn(buttonVariants({ size, variant, className }))}
                ref={ref}
                {...props}
            />
        );
    }
);
Button.displayName = "Button";

export { Button, buttonVariants };
